import React, { useEffect, useRef, useState } from "react";
import { LuCalendarPlus } from "react-icons/lu";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import { getBarChartFormattedData, getStepSize } from "../../services/commonServices";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const AttendanceChart = ({ attendanceData }) => {

function getOrdinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

let { labels, datasetLabels, data1, data2, data3 } =
  getBarChartFormattedData(attendanceData);

const canvasRef = useRef(null);
const [gradientData, setGradientData] = useState(null);

// ---- 🆕 Dummy Data Logic ----
const today = new Date();
const year = today.getFullYear();
const month = today.getMonth();
const daysInMonth = new Date(year, month + 1, 0).getDate();

const shortMonth = today.toLocaleString("en-US", { month: "short" });

const labelCount = labels.length;
  const actualDataLength = labels.length;
  const remainingDays = daysInMonth - actualDataLength;

  if (remainingDays > 0) {
    for (let i = 0; i < remainingDays; i++) {
      labels.push(getOrdinal(i+labelCount+1) + " " +shortMonth);
      data1.push(0);
      data2.push(0);
      data3.push(0);
    }
  }

  const maxValue = Math.max(
  ...data1,
  ...data2,
  ...data3
);

  // Dynamic Percentage Calculation
  const categoryPercentage = Math.max(0.3, Math.min(0.9, 1 - labelCount * 0.02));
  const barPercentage = Math.max(0.3, Math.min(0.9, 1 - labelCount * 0.015));
  const dynamicBarPercentage = Math.max(0.2, Math.min(0.9, 0.9 - labelCount * 0.02));

  const colors = ["#31BB79", "#FFDD81", "#FD5F5F"];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const gradient = ctx.createLinearGradient(0, 10, 0, 300);
    gradient.addColorStop(0, "#167145");
    gradient.addColorStop(0.5, "#167145");
    gradient.addColorStop(0.8, "#31BB79");
    gradient.addColorStop(1, "#31BB79");

    const gradient1 = ctx.createLinearGradient(0, 10, 0, 300);
    gradient1.addColorStop(0, "#FFBD08");
    gradient1.addColorStop(0.5, "#FFBD08");
    gradient1.addColorStop(0.8, "#FFDD81");
    gradient1.addColorStop(1, "#FFDD81");

    const gradient2 = ctx.createLinearGradient(0, 10, 0, 300);
    gradient2.addColorStop(0, "#E33A3A");
    gradient2.addColorStop(0.5, "#E33A3A");
    gradient2.addColorStop(0.8, "#FD5F5F");
    gradient2.addColorStop(1, "#FD5F5F");

    // ---- 🆕 Background color arrays (dummy ke liye transparent) ----
    const bg1 = data1.map((v, i) =>
      i < actualDataLength ? gradient : "rgba(0,0,0,0)"
    );
    const bg2 = data2.map((v, i) =>
      i < actualDataLength ? gradient1 : "rgba(0,0,0,0)"
    );
    const bg3 = data3.map((v, i) =>
      i < actualDataLength ? gradient2 : "rgba(0,0,0,0)"
    );

    setGradientData({
      labels,
      datasets: [
        {
          label: "On Time",
          data: data1,
          backgroundColor: bg1,
          stack: "attendance",
          barThickness: 37,
          borderSkipped: false,
          categoryPercentage: 1,
          barPercentage: 1,
        },
        {
          label: "Late Arrival",
          data: data2,
          backgroundColor: bg2,
          stack: "attendance",
          barThickness: 37,
          borderSkipped: false,
          categoryPercentage: 1,
          barPercentage: 1,
        },
        {
          label: "Absent",
          data: data3,
          backgroundColor: bg3,
          stack: "attendance",
          barThickness: 37,
          borderSkipped: false,
          categoryPercentage: 1,
          barPercentage: 1,
        },
      ],
    });
  }, []);

  const borderRadius = 12;
  const borderRadiusAllCorners = {
    topLeft: borderRadius,
    topRight: borderRadius,
    bottomLeft: borderRadius,
    bottomRight: borderRadius,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      bar: {
        borderRadius: borderRadiusAllCorners,
        borderWidth: 10,
        // borderWidth: labelCount > 20 ? 10 : labelCount > 15 ? 15 : 15,
        borderColor: "transparent",
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: "index",
        backgroundColor: "#fff",
        titleColor: "#000",
        bodyColor: "#000",
        cornerRadius: 10,
        usePointStyle: true,
        boxWidth: 8,
        intersect: false,
        titleFont: {
          family: "Outfit",
          size: 14,
        },
        bodyFont: {
          family: "Outfit",
          size: 13,
        },
        footerFont: {
          family: "Outfit",
          size: 12,
        },
       
        filter: (tooltipItem) => {
          return labels[tooltipItem.dataIndex] !== ""; // Dummy days ko skip karo
        },

        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.parsed.y}`,
        },
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue(
            "--primary-text-color"
          ),
          font: {
            family: "outfit", // 👈 x-axis labels font family
            size: 12,
          },
        },
        grid: { display: false },
      },
      y: {
        stacked: true,
        ticks: {
          stepSize: getStepSize(maxValue),
          color: getComputedStyle(document.documentElement).getPropertyValue(
            "--primary-text-color"
          ),
        },
        beginAtZero: true,
        grid: { display: false },
      },
    },
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "800px",
        minWidth: "500px",
        height: "350px",
        paddingBottom: "30px",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: "none" }}
        width={400}
        height={400}
      />
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ position: "relative", top: "-25px" }}
      >
        <div className="d-flex align-items-center justify-content-start gap-2">
          <div className="universal-btn">
            <LuCalendarPlus />
          </div>
          <h3 className="color-white"> Employee Attendance Overview</h3>
        </div>
        <div className="d-flex align-items-center justify-content-start gap-2">
          {datasetLabels.map((label, index) => (
            <div
              key={label}
              style={{
                fontSize: "14px",
                wordWrap: "nowrap",
                cursor: "pointer",
              }}
              className="color-white"
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: colors[index],
                    display: "inline-block",
                  }}
                />
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {gradientData && labels && <Bar data={gradientData} options={options} />}
    </div>
  );
};

export default AttendanceChart;
